import { useState, useEffect, useCallback } from 'react'
import { getApiUrl, fetchWithAuth } from '../../api'
import { useNavigate, useParams } from 'react-router-dom'

interface BoardInfo {
  id: number
  slug: string
  name: string
  theme: 'table' | 'card' | 'gallery'
  has_file: number
  has_image: number
}

interface Post {
  id: number
  title: string
  created_at: string
  author?: string
  thumbnail_url?: string | null
  file_count?: number
}

interface PostsResponse {
  items: Post[]      // posts → items
  total: number
  page: number
  size: number       // total_pages 없음, size로 계산
}

const PER_PAGE = 15

export default function BoardList() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  const [board, setBoard] = useState<BoardInfo | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  const fetchBoard = useCallback(async () => {
    if (!slug) return
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/board/${slug}`))
      if (res.ok) setBoard(await res.json())
    } catch {}
  }, [slug])

  const fetchPosts = useCallback(async () => {
    if (!slug) return
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PER_PAGE),
        ...(search ? { search } : {}),
      })
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/board/${slug}/post?${params}`))
      if (res.ok) {
        const data: PostsResponse = await res.json()
        setPosts(data.items ?? [])
        setTotal(data.total)
        setTotalPages(Math.ceil(data.total / PER_PAGE))
      }
    } catch {}
    setLoading(false)
  }, [slug, page, search])

  useEffect(() => { fetchBoard() }, [fetchBoard])
  useEffect(() => { fetchPosts() }, [fetchPosts])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput)
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })

  // 페이지네이션 번호 계산 (최대 10개)
  const getPageNumbers = () => {
    const group = Math.floor((page - 1) / 10)
    const start = group * 10 + 1
    const end = Math.min(start + 9, totalPages)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  const theme = board?.theme ?? 'table'

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 헤더 */}
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h1 className="text-[26px] font-extrabold text-[#887267] mb-1">{board?.name ?? ''}</h1>
            <p className="text-[13px] text-gray-400">총 {total}개의 게시글</p>
          </div>
          <button
            onClick={() => navigate(`/board/${slug}/register`)}
            className="bg-[#887267] text-white text-[13px] font-semibold px-5 py-2.5 rounded-xl border-none cursor-pointer hover:bg-[#6b5248] transition-colors shadow-[0_4px_12px_rgba(112,48,160,0.2)]"
          >
            + 글쓰기
          </button>
        </div>

        {/* 검색 */}
        <form onSubmit={handleSearch} className="mb-6 flex gap-2">
          <input
            type="text"
            placeholder="검색어를 입력하세요"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="flex-1 px-4 py-2.5 text-[13px] border border-gray-200 rounded-xl outline-none focus:border-[#887267] transition-colors bg-white"
          />
          <button
            type="submit"
            className="px-5 py-2.5 bg-white text-[#887267] text-[13px] font-semibold border border-[#887267] rounded-xl cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            검색
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchInput(''); setPage(1) }}
              className="px-4 py-2.5 text-[13px] text-gray-400 border border-gray-200 rounded-xl bg-white cursor-pointer hover:bg-gray-50"
            >
              초기화
            </button>
          )}
        </form>

        {/* 로딩 */}
        {loading ? (
          <div className="flex justify-center items-center py-20 text-gray-300 text-sm">불러오는 중...</div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col justify-center items-center py-20 text-gray-300">
            <p className="text-[40px] mb-3">📭</p>
            <p className="text-[14px]">게시글이 없습니다.</p>
          </div>
        ) : (
          <>
            {/* ───── TABLE ───── */}
            {theme === 'table' && (
              <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#e8e0dc] bg-[#ede0d8]">
                      <th className="text-[12px] text-[#5a3d32] font-semibold px-5 py-3 text-left w-[70px]">번호</th>
                      <th className="text-[12px] text-[#5a3d32] font-semibold px-5 py-3 text-left w-[130px]">썸네일</th>
                      <th className="text-[12px] text-[#5a3d32] font-semibold px-5 py-3 text-left">제목</th>
                      {posts[0]?.author !== undefined && (
                        <th className="text-[12px] text-[#5a3d32] font-semibold px-5 py-3 text-center w-[100px]">작성자</th>
                      )}
                      <th className="text-[12px] text-[#5a3d32] font-semibold px-5 py-3 text-center w-[120px]">작성일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((post, idx) => (
                      <tr
                        key={post.id}
                        className="border-b border-[#f5f2f0] last:border-b-0 hover:bg-[#fdf8f0] cursor-pointer transition-colors"
                        onClick={() => navigate(`/board/${slug}/posts/${post.id}`)}
                      >
                        <td className="px-5 py-3 text-[12px] text-gray-300 text-center">
                          {total - (page - 1) * PER_PAGE - idx}
                        </td>
                        
                        {post.thumbnail_url ? (
                          <td className="px-5 py-3 text-[12px] text-gray-300 text-center">
                    <img
                      src={post.thumbnail_url}
                      className="w-full h-full object-contain p-1"
                    />
                    </td>

                  ) : (
                    <td className="px-5 py-3 text-[12px] text-gray-300 text-center">
                    <div className="flex flex-col items-center gap-1 text-gray-300">
                      <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="currentColor" strokeWidth={1.5}>
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                    </td>
                  )}
                  
                        <td className="px-5 py-3 text-[13px] text-[#2a1f1a] font-medium">
                          <span className="hover:text-[#887267] transition-colors">{post.title}</span>
                          {post.file_count && post.file_count > 0 ? (
                            <span className="ml-2 text-[11px] text-[#887267]">📎</span>
                          ) : null}
                        </td>
                        {post.author !== undefined && (
                          <td className="px-5 py-3 text-[12px] text-gray-400 text-center">{post.author}</td>
                        )}
                        <td className="px-5 py-3 text-[12px] text-gray-400 text-center">{formatDate(post.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ───── CARD ───── */}
            {theme === 'card' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {posts.map(post => (
                  <div
                    key={post.id}
                    className="bg-white rounded-2xl border border-[#e8e0dc] p-5 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)] cursor-pointer hover:shadow-[0_4px_20px_rgba(112,48,160,0.12)] hover:-translate-y-0.5 transition-all"
                    onClick={() => navigate(`/board/${slug}/posts/${post.id}`)}
                  >
                    {post.thumbnail_url && (
                      <div className="w-full h-[140px] rounded-xl overflow-hidden mb-3 bg-gray-100">
                        <img src={post.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <p className="text-[14px] font-bold text-[#2a1f1a] mb-2 line-clamp-2 leading-snug">{post.title}</p>
                    <div className="flex items-center justify-between mt-3">
                      {post.author && <span className="text-[11px] text-gray-400">{post.author}</span>}
                      <span className="text-[11px] text-gray-300 ml-auto">{formatDate(post.created_at)}</span>
                    </div>
                    {post.file_count && post.file_count > 0 ? (
                      <span className="text-[11px] text-[#887267] mt-1 block">📎 첨부 {post.file_count}개</span>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            {/* ───── GALLERY ───── */}
            {theme === 'gallery' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {posts.map(post => (
                  <div
                    key={post.id}
                    className="relative group cursor-pointer rounded-2xl overflow-hidden bg-gray-100 aspect-square shadow-[0_2px_8px_rgba(112,48,160,0.08)]"
                    onClick={() => navigate(`/board/${slug}/posts/${post.id}`)}
                  >
                    {post.thumbnail_url ? (
                      <img src={post.thumbnail_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-200 text-[40px]">🖼️</div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end p-3">
                      <p className="text-white text-[12px] font-semibold line-clamp-2">{post.title}</p>
                    </div>
                    <div className="absolute top-2 right-2 bg-black/30 text-white text-[10px] px-1.5 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                      {formatDate(post.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1 mt-8">
            {/* 이전 그룹 */}
            {Math.floor((page - 1) / 10) > 0 && (
              <button
                onClick={() => setPage(Math.floor((page - 1) / 10) * 10)}
                className="w-8 h-8 text-[13px] text-gray-400 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-[#f5f2f0] hover:text-[#887267] hover:border-[#887267] transition-colors"
              >
                ‹
              </button>
            )}

            {getPageNumbers().map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-8 h-8 text-[13px] rounded-lg border cursor-pointer transition-colors ${
                  page === p
                    ? 'bg-[#887267] text-white border-[#887267] font-bold'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-[#f5f2f0] hover:text-[#887267] hover:border-[#887267]'
                }`}
              >
                {p}
              </button>
            ))}

            {/* 다음 그룹 */}
            {Math.floor((page - 1) / 10) < Math.floor((totalPages - 1) / 10) && (
              <button
                onClick={() => setPage(Math.floor((page - 1) / 10) * 10 + 11)}
                className="w-8 h-8 text-[13px] text-gray-400 border border-gray-200 rounded-lg bg-white cursor-pointer hover:bg-[#f5f2f0] hover:text-[#887267] hover:border-[#887267] transition-colors"
              >
                ›
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  )
}